/* istanbul ignore file */
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    Signal,
    ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { TravelType } from '@app/enums/travel-type.enum';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import { MedpaceDatepickerComponent } from '@components/atoms/medpace-datepicker/medpace-datepicker.component';
import { MedpaceTextareaAutocompleteComponent } from '@components/atoms/textarea-autocomplete/medpace-textarea-autocomplete.component';
import { CardMode } from '@components/card/card.component';
import { SingleFlightFormGroup } from '@components/request/travel-request-card/form-groups/detail-form-groups/travel/flight-request.formgroup';
import {
    MdsAutocompleteComponent,
    MdsButtonToggle,
    MdsButtonToggleGroupComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { MedpaceAutocompleteObject } from '@models/medpaceAutocompleteObject';
import { TravelViewModelService } from '@services/travel-view-model/travel-view-model.service';
import { PersistentFormControl } from '@utility/persistent-forms';
import { getUniqueObjects, getUniqueStrings } from '@utility/utility';
import { Observable, startWith, switchMap, tap } from 'rxjs';

@Component({
    selector: 'medpace-flight-details',
    templateUrl: './flight-details.component.html',
    styleUrls: ['./flight-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        dateFormatPipe,
        AlertComponent,
        MedpaceDatepickerComponent,
        MedpaceTextareaAutocompleteComponent,
        MatRadioModule,
        ReactiveFormsModule,
        MdsAutocompleteComponent,
        MdsButtonToggleGroupComponent,
    ],
})
export class FlightDetailsComponent {
    private readonly travelViewModelService = inject(TravelViewModelService);

    public readonly mode: InputSignal<CardMode> = input.required();
    public readonly noFlightOption: InputSignal<boolean> = input(true);
    public readonly formGroup: InputSignal<SingleFlightFormGroup> = input.required();

    public readonly TravelType = TravelType;

    public readonly flightOptionButtons: Signal<MdsButtonToggle[]> = computed(() => {
        const buttons: MdsButtonToggle[] = [];

        const noFlightOption: MdsButtonToggle = {
            id: 'noFlight_Radio',
            value: TravelType.NO_FLIGHT,
            name: 'No flight',
        };
        buttons.push(
            ...[
                {
                    id: 'roundTrip_Radio',
                    value: TravelType.ROUND_TRIP,
                    name: 'Roundtrip',
                },
                {
                    id: 'oneWay_Radio',
                    value: TravelType.ONE_WAY,
                    name: 'One way',
                },
            ]
        );

        if (this.noFlightOption()) buttons.push(noFlightOption);

        return buttons;
    });

    private readonly disableReturnDate = effect(() => {
        const formGroup = this.formGroup();
        if (formGroup.controls.travelType.value === TravelType.ONE_WAY) {
            formGroup.controls.returnDate.disable({ emitEvent: false, persistent: true });
        }
    });

    public readonly autocompleteOptions: Signal<{
        airportOrigin: string[];
        airportDestination: string[];
        specialRequests: MedpaceAutocompleteObject[];
    }> = computed(() => {
        const value = this.travelViewModelService.travelCardViewModel()?.historyData.flight;
        return {
            airportOrigin: getUniqueStrings(
                value?.filter((data) => !!data.airportOrigin).map((data) => data.airportOrigin) ?? []
            ),
            airportDestination: getUniqueStrings(
                value?.filter((data) => !!data.airportDestination).map((data) => data.airportDestination) ?? []
            ),
            specialRequests: getUniqueObjects(
                value
                    ?.filter((data) => !!data.specialRequest)
                    .map(
                        (data) =>
                            <MedpaceAutocompleteObject>{ requestDate: data.requestDate, value: data.specialRequest }
                    ) ?? []
            ),
        };
    });

    public readonly travelTypeChange$: Observable<TravelType | null> = toObservable(this.formGroup).pipe(
        switchMap((formGroup) =>
            formGroup.controls.travelType.valueChanges.pipe(startWith(formGroup.controls.travelType.getRawValue()))
        ),
        tap((travelType) => {
            let controlsToEnable: PersistentFormControl[] = [];
            let controlsToDisable: PersistentFormControl[] = [];
            if (travelType === TravelType.ROUND_TRIP) {
                controlsToEnable = Object.values(this.formGroup().controls).filter(
                    (control) => control !== this.formGroup().controls.travelType
                );
                controlsToDisable = [];
            } else if (travelType === TravelType.ONE_WAY) {
                controlsToEnable = Object.values(this.formGroup().controls).filter(
                    (control) =>
                        control !== this.formGroup().controls.travelType &&
                        control !== this.formGroup().controls.returnDate
                );
                controlsToDisable = [this.formGroup().controls.returnDate];
            } else {
                // No_Flight
                controlsToEnable = Object.values(this.formGroup().controls).filter(
                    (control) => control === this.formGroup().controls.travelType
                );
                controlsToDisable = Object.values(this.formGroup().controls).filter(
                    (control) => control !== this.formGroup().controls.travelType
                );
            }
            controlsToEnable.forEach((control) =>
                control.enable({
                    breakPersistence: true,
                    onlySelf: true,
                    emitEvent: false,
                })
            );
            controlsToDisable.forEach((control) =>
                control.disable({
                    persistent: true,
                    onlySelf: true,
                    emitEvent: false,
                })
            );
        })
    );
}
